import React, { useState } from 'react';
import { Modal, Form, Upload, Button, Radio, Space, Table, Result, Alert, Progress, Descriptions, Steps, message, Tag, Pagination } from 'antd';
import dayjs from 'dayjs';
import { DownloadOutlined, CheckCircleFilled } from '@ant-design/icons';
import { batchPrecheck, batchImport, getBatchReport, createBatchWorkspaceByUpload, downloadBatchErrorReport } from '../../../services/debt';
import { PAGINATION_DEFAULT_PAGE_SIZE, PAGINATION_ENABLE_SIZE_CHANGER, PAGINATION_PAGE_SIZE_OPTIONS } from '../../../config/theme';
import { useBreakpoint } from '../../../hooks/useBreakpoint';

const modeLabel = (m) => {
  if (m === 'full') return '全量新增';
  if (m === 'incremental') return '增量仅新增';
  if (m === 'incremental_full_update') return '增量全更新';
  return String(m || '');
};
const normalizePageSize = (size) => (PAGINATION_ENABLE_SIZE_CHANGER ? Number(size || PAGINATION_DEFAULT_PAGE_SIZE) : PAGINATION_DEFAULT_PAGE_SIZE);
const statusLabel = (s) => {
  if (s === 'RUNNING') return '进行中';
  if (s === 'COMPLETED') return '已完成';
  if (s === 'FAILED') return '失败';
  return String(s || '');
};
const statusColor = (s) => {
  if (s === 'RUNNING') return 'blue';
  if (s === 'COMPLETED') return 'green';
  if (s === 'FAILED') return 'red';
  return 'default';
};
const precheckColumns = [
  { title: '工作表', dataIndex: 'sheet', key: 'sheet' },
  { title: '行号', dataIndex: 'row', key: 'row' },
  { title: '字段', dataIndex: 'field', key: 'field' },
  { title: '原因', dataIndex: 'reason', key: 'reason' },
];

export const UploadDialog = ({ open, onClose, onConfirm }) => {
  const { isMobile } = useBreakpoint();
  const [form] = Form.useForm();
  React.useEffect(() => {
    if (open) {
      form.resetFields();
    }
  }, [open, form]);
  const handleConfirm = async () => {
    const v = form.getFieldsValue();
    const f = Array.isArray(v.upload?.fileList) && v.upload.fileList[0] ? v.upload.fileList[0] : null;
    const fileName = f ? f.name : 'mock.xlsx';
    try {
      if (!f) {
        message.error('请选择要上传的数据文件');
        return;
      }
      const fileObj = f.originFileObj || f;
      const { data } = await createBatchWorkspaceByUpload(fileObj, v.mode || 'full');
      onConfirm && onConfirm({
        batchId: data?.batchId,
        fileName: data?.fileName || fileName,
        totalRows: Number(data?.totalRows || 0),
        worksheets: Array.isArray(data?.worksheets) ? data.worksheets : [],
        mode: data?.mode || v.mode || 'full',
        uploadedAt: data?.uploadedAt || dayjs().toISOString(),
      });
      message.success('工作区创建成功');
    } catch (e) {
      message.error('工作区创建失败');
    }
  };
  return (
    <Modal
      title="上传数据文件"
      open={open}
      onCancel={onClose}
      footer={null}
      width={isMobile ? '92vw' : 720}
      style={isMobile ? { top: 12 } : undefined}
      styles={isMobile ? { body: { maxHeight: '72vh', overflowY: 'auto' } } : undefined}
      destroyOnClose
    >
      <Form form={form} layout={isMobile ? 'vertical' : 'inline'} style={{ marginBottom: 16 }}>
        <Form.Item name="upload" label="上传文件">
          <Upload beforeUpload={() => false} maxCount={1} accept=".xlsx,.xls">
            <Button block={isMobile}>选择文件</Button>
          </Upload>
        </Form.Item>
        <Form.Item name="mode" label="批量发布模式" initialValue="full">
          <Radio.Group style={{ width: isMobile ? '100%' : undefined }} options={[
            { label: '全量新增', value: 'full' },
            { label: '增量仅新增', value: 'incremental' },
            { label: '增量全更新', value: 'incremental_full_update' },
          ]} />
        </Form.Item>
        <Form.Item>
          <Button type="primary" onClick={handleConfirm} block={isMobile}>确认上传</Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export const PrecheckDialog = ({ open, onClose, workspace }) => {
  const [summary, setSummary] = useState({ totalRows: 0, errorRows: 0 });
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (!open) {
      return;
    }
    const rows = Number(workspace?.totalRows || 0);
    setSummary({ totalRows: rows, errorRows: 0 });
    setErrors([]);
    if (!workspace?.batchId) {
      message.error('请先上传文件创建工作区');
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const payload = { batch_id: workspace.batchId };
        const { data } = await batchPrecheck(payload);
        const es = Array.isArray(data?.errors) ? data.errors : [];
        setErrors(es);
        setSummary({ totalRows: Number(data?.totalRows || rows), errorRows: es.length });
      } catch (e) {
        message.error('预检失败');
      } finally {
        setLoading(false);
      }
    })();
  }, [open, workspace?.batchId, workspace?.totalRows]);

  return (
    <Modal title="预检（仅校验）" open={open} onCancel={onClose} footer={null} width={860}>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message={`文件：${workspace?.fileName || '未选择'}；总行数：${summary.totalRows}，错误行数：${summary.errorRows}`}
      />
      <Table
        rowKey={(_, i) => String(i)}
        dataSource={errors}
        columns={precheckColumns}
        pagination={{
          pageSize: PAGINATION_DEFAULT_PAGE_SIZE,
          showSizeChanger: PAGINATION_ENABLE_SIZE_CHANGER,
          pageSizeOptions: PAGINATION_PAGE_SIZE_OPTIONS,
        }}
        loading={loading}
      />
      {loading ? (
        <Result status="info" title="预检进行中..." />
      ) : summary.errorRows > 0 ? (
        <Result status="warning" title="预检发现错误，不落库" />
      ) : (
        <Result status="success" title="预检通过，可进行导入" />
      )}
    </Modal>
  );
};

export const ImportDialog = ({ open, onClose, workspace, onBatchAdd }) => {
  const [started, setStarted] = useState(false);
  const [status, setStatus] = useState('PENDING');
  const [batchId, setBatchId] = useState('');
  const [resultOk, setResultOk] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [precheckErrors, setPrecheckErrors] = useState([]);
  const [sheetStats, setSheetStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  React.useEffect(() => {
    if (open) {
      setStarted(false);
      setStatus('PENDING');
      setBatchId('');
      setResultOk(false);
      setCurrentStep(0);
      setPrecheckErrors([]);
      setSheetStats(null);
      setLoading(false);
      setErrorMessage('');
    }
  }, [open, workspace?.batchId]);
  const start = async () => {
    const rows = Number(workspace?.totalRows || 12);
    setStarted(true);
    setStatus('PENDING');
    setResultOk(false);
    setCurrentStep(0);
    setPrecheckErrors([]);
    setSheetStats(null);
    setErrorMessage('');
    if (!workspace?.batchId) {
      message.error('请先上传文件创建工作区');
      return;
    }
    try {
      setLoading(true);
      const prePayload = { batch_id: workspace.batchId };
      const { data: pre } = await batchPrecheck(prePayload);
      const es = Array.isArray(pre?.errors) ? pre.errors : [];
      const preTotalRows = Number(pre?.totalRows || rows);
      if (es.length > 0) {
        setPrecheckErrors(es);
        setErrorMessage(`预检发现 ${es.length} 条错误，已阻止导入，请修正文件后重新上传`);
        setStatus('FAILED');
        setResultOk(false);
        return;
      }
      setCurrentStep(1);
      const mode = workspace?.mode || 'full';
      const bid = workspace?.batchId || `B${dayjs().format('YYYYMMDDHHmmss')}`;
      setBatchId(bid);
      const payload = { batch_id: bid, mode };
      const { data } = await batchImport(payload);
      const item = {
        batchId: String(data?.batchId || bid),
        mode,
        totalRows: Number(data?.totalRows || preTotalRows),
        importedRows: Number(data?.importedRows || rows),
        status: String(data?.status || 'COMPLETED'),
        createdAt: dayjs().toISOString(),
      };
      setStatus(item.status);
      setResultOk(item.status === 'COMPLETED');
      setSheetStats(data?.sheetStats || null);
      onBatchAdd && onBatchAdd(item);
    } catch (e) {
      message.error('导入失败');
      setStatus('FAILED');
      setResultOk(false);
      setErrorMessage(e?.message || '导入失败');
    } finally {
      setLoading(false);
    }
  };
  return (
    <Modal title="导入数据" open={open} onCancel={onClose} footer={null} width={720}>
      {!started ? (
        <>
          <Alert type="info" showIcon style={{ marginBottom: 12 }} message={`文件：${workspace?.fileName || '未选择'}；模式：${modeLabel(workspace?.mode || 'full')}`} />
          <Space>
            <Button type="primary" onClick={start} disabled={!workspace}>开始导入</Button>
          </Space>
        </>
      ) : (
        <>
          <Steps
            style={{ marginTop: 8, marginBottom: 16 }}
            current={currentStep}
            items={[
              {
                title: '步骤1 预检',
                status:
                  precheckErrors.length > 0
                    ? 'error'
                    : currentStep > 0 || status === 'COMPLETED'
                    ? 'finish'
                    : loading && currentStep === 0
                    ? 'process'
                    : 'wait',
              },
              {
                title: '步骤2 导入',
                status:
                  status === 'COMPLETED'
                    ? 'finish'
                    : precheckErrors.length > 0
                    ? 'wait'
                    : loading && currentStep === 1
                    ? 'process'
                    : status === 'FAILED'
                    ? 'error'
                    : 'wait',
              },
            ]}
          />
          <div style={{ marginTop: 16 }}>
            {loading && (
              <Result
                status="info"
                title={currentStep === 0 ? '正在进行预检...' : '正在导入数据...'}
              />
            )}
            {!loading && precheckErrors.length > 0 && (
              <>
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 12 }}
                  message={errorMessage || '预检发现错误，已阻止导入'}
                />
                <Table
                  rowKey={(_, i) => String(i)}
                  dataSource={precheckErrors}
                  columns={precheckColumns}
                  pagination={{
                    pageSize: PAGINATION_DEFAULT_PAGE_SIZE,
                    showSizeChanger: PAGINATION_ENABLE_SIZE_CHANGER,
                    pageSizeOptions: PAGINATION_PAGE_SIZE_OPTIONS,
                  }}
                />
                <Result status="error" title="导入已取消（预检未通过）" />
              </>
            )}
            {!loading && precheckErrors.length === 0 && status === 'COMPLETED' && (
              <>
                <Result status="success" title="导入完成" subTitle={`批次ID：${batchId}`} />
                {sheetStats && (
                  <Descriptions bordered size="small" column={2} style={{ marginTop: 12 }}>
                    {Object.entries(sheetStats).map(([k, v]) => (
                      <Descriptions.Item key={k} label={k}>{Number(v || 0)}</Descriptions.Item>
                    ))}
                  </Descriptions>
                )}
              </>
            )}
            {!loading && precheckErrors.length === 0 && status === 'FAILED' && (
              <Result status="error" title="导入失败" subTitle={errorMessage || `批次ID：${batchId}`} />
            )}
          </div>
          {resultOk && status === 'COMPLETED' && (
            <Alert
              style={{ marginTop: 8 }}
              type="success"
              message="事务单位按主债权/目标债权执行，失败不影响其他批次项"
              showIcon
            />
          )}
        </>
      )}
    </Modal>
  );
};

export const ReportDialog = ({ open, onClose, record }) => {
  const { isMobile } = useBreakpoint();
  const [dataSource, setDataSource] = useState({
    summary: {
      batchId: record?.batchId || '',
      mode: record?.mode || 'full',
      status: record?.status || 'PENDING',
      totalRows: record?.totalRows || 0,
      importedRows: record?.importedRows || 0,
      errorRows: 0,
      percent: 0,
      createdAt: record?.createdAt || dayjs().toISOString(),
    },
    errors: [],
  });
  const [loading, setLoading] = useState(false);
  React.useEffect(() => {
    (async () => {
      if (!open || !record?.batchId) return;
      setLoading(true);
      try {
        const { data } = await getBatchReport(record.batchId);
        setDataSource({
          summary: data?.summary || {
            batchId: record?.batchId || '',
            mode: record?.mode || 'full',
            status: record?.status || 'PENDING',
            totalRows: record?.totalRows || 0,
            importedRows: record?.importedRows || 0,
            errorRows: 0,
            percent: 0,
            createdAt: record?.createdAt || dayjs().toISOString(),
          },
          errors: Array.isArray(data?.errors) ? data.errors : [],
        });
      } catch (e) {
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, record?.batchId]);
  const { summary, errors } = dataSource;
  const sheetStatsRows = summary && summary.sheetStats && typeof summary.sheetStats === 'object'
    ? Object.entries(summary.sheetStats).map(([k, v]) => ({ sheet: k, count: Number(v || 0) }))
    : [];
  const handleDownload = async () => {
    try {
      const res = await downloadBatchErrorReport(summary.batchId);
      const blob = res && res.data instanceof Blob ? res.data : new Blob([res.data], { type: res.headers && res.headers['content-type'] ? res.headers['content-type'] : 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const ts = dayjs().format('YYYYMMDDHHmmss');
      a.href = url;
      a.download = `错误报告-${summary.batchId}-${ts}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      message.success('已下载错误报告');
    } catch (e) {
      message.error('下载失败');
    }
  };
  return (
    <Modal
      title="批次报表"
      open={open}
      onCancel={onClose}
      footer={null}
      width={isMobile ? '92vw' : 920}
      style={isMobile ? { top: 12 } : undefined}
      styles={isMobile ? { body: { maxHeight: '72vh', overflowY: 'auto' } } : undefined}
    >
      {record ? (
        <>
          <Descriptions
            bordered
            size="small"
            column={isMobile ? 1 : 3}
            labelStyle={isMobile ? { width: 96, minWidth: 96, whiteSpace: 'nowrap' } : undefined}
            contentStyle={isMobile ? { wordBreak: 'break-word' } : undefined}
          >
            <Descriptions.Item label="批次ID">{summary.batchId}</Descriptions.Item>
            <Descriptions.Item label="模式">{modeLabel(summary.mode)}</Descriptions.Item>
            <Descriptions.Item label="状态">
              <Tag color={statusColor(summary.status)}>{statusLabel(summary.status)}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="总行数">{summary.totalRows}</Descriptions.Item>
            <Descriptions.Item label="已导入">{summary.importedRows}</Descriptions.Item>
            <Descriptions.Item label="错误行数">{summary.errorRows}</Descriptions.Item>
            <Descriptions.Item label="进度">{`${summary.percent}%`}</Descriptions.Item>
            <Descriptions.Item label="创建时间">{summary.createdAt ? dayjs(summary.createdAt).format('YYYY-MM-DD HH:mm:ss') : ''}</Descriptions.Item>
            <Descriptions.Item label="错误报告">
              <Space>
                <Button size="small" icon={<DownloadOutlined />} onClick={handleDownload} disabled={Number(summary.errorRows || 0) <= 0}>下载错误报告</Button>
              </Space>
            </Descriptions.Item>
            {sheetStatsRows.length > 0 && (
              <Descriptions.Item label="工作表记录数" span={isMobile ? 1 : 3}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
                  <div style={{ flex: '0 1 auto', width: isMobile ? '100%' : 'auto', maxWidth: isMobile ? '100%' : 360 }}>
                    <div style={{ border: '1px solid var(--border-color-secondary)', borderRadius: 4, overflow: 'hidden' }}>
                      <div style={{ display: 'flex', gap: 8, padding: '6px 8px', background: 'var(--bg-color-secondary)', fontWeight: 500 }}>
                        <div style={{ flex: '1 1 auto', minWidth: 0 }}>工作表</div>
                        <div style={{ width: 72, textAlign: 'right' }}>记录条数</div>
                      </div>
                      {sheetStatsRows.map((r) => (
                        <div key={r.sheet} style={{ display: 'flex', gap: 8, padding: '6px 8px', borderTop: '1px solid var(--border-color-secondary)' }}>
                          <div style={{ flex: '1 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {r.sheet}
                          </div>
                          <div style={{ width: 72, textAlign: 'right' }} className="u-tabular-nums">
                            {r.count}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  {(!Array.isArray(errors) || errors.length === 0) && (
                    <div style={{ flex: '0 0 auto', minWidth: isMobile ? '100%' : 140, display: 'flex', justifyContent: isMobile ? 'flex-start' : 'center' }}>
                      <Space size={8}>
                        <CheckCircleFilled style={{ color: '#52c41a', fontSize: 16 }} />
                        <span style={{ color: 'var(--text-color-secondary)' }}>无错误</span>
                      </Space>
                    </div>
                  )}
                </div>
              </Descriptions.Item>
            )}
          </Descriptions>
          {summary.status === 'RUNNING' && <Progress style={{ marginTop: 12 }} percent={summary.percent} />}
          <div style={{ marginTop: 12 }}>
            {Array.isArray(errors) && errors.length > 0 ? (
              <>
                <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={`发现 ${errors.length} 条错误`} />
                <Table
                  loading={loading}
                  size={isMobile ? 'small' : 'middle'}
                  rowKey={(_, i) => String(i)}
                  dataSource={errors}
                  columns={precheckColumns}
                  scroll={{ x: 'max-content' }}
                  pagination={{
                    pageSize: PAGINATION_DEFAULT_PAGE_SIZE,
                    showSizeChanger: PAGINATION_ENABLE_SIZE_CHANGER,
                    pageSizeOptions: PAGINATION_PAGE_SIZE_OPTIONS,
                  }}
                />
              </>
            ) : (
              sheetStatsRows.length > 0 ? null : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px 0' }}>
                  <Space size={8}>
                    <CheckCircleFilled style={{ color: '#52c41a', fontSize: 16 }} />
                    <span style={{ color: 'var(--text-color-secondary)' }}>无错误</span>
                  </Space>
                </div>
              )
            )}
          </div>
        </>
      ) : null}
    </Modal>
  );
};
